<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import webapp from '../services/webapp'
import { nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from '@/composables/i18n'
import { usePvpGame } from '@/composables/game/pvp'
import { useScreenSize } from '@/composables/helpers/screen-size'
import { PVP_GAME_STATUS } from '@/types/shared/pvp'
import { useAuth } from '@/composables/auth'
import api from '@/services/api'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { usePlayer } from '@/composables/game/player'
import { Rive, StateMachineInput } from '@rive-app/canvas'
import type { LiveOpsEventTemplate, LiveOpsUserEvent } from '@/types/live-ops'
import { useRouter } from 'vue-router'

const { profile } = useAuth()

const WebApp = Telegram.WebApp
const { changeLanguage, selectedLanguage } = useI18n()
const { updatePlayer } = usePlayer()
const lang = ref(selectedLanguage.value)
const { rawAddress } = useWalletConnect()
const router = useRouter()

watch(lang, lang => {
  changeLanguage(lang)
})

const { matchStatus } = usePvpGame()
watch(matchStatus, matchStatus => {
  if (matchStatus === PVP_GAME_STATUS.PENDING) return
  if (matchStatus === PVP_GAME_STATUS.DRAW) {
    webapp.showAlert(`It's a draw!`)
  } else {
    webapp.showAlert(`You ${matchStatus}!`)
  }
})

const { width, height } = useScreenSize()

function fullScreen() {
  WebApp.requestFullscreen()
}

const balances = ref<Awaited<ReturnType<typeof api.getJettons>>['balances']>([])
watch(rawAddress, async (rawAddress) => {
  if (!rawAddress) return
  const response = await api.getJettons(rawAddress)
  balances.value = response.balances

}, { immediate: true })

async function getFreeStars() {
  const { playerStats } = await api.increaseStars()
  updatePlayer(playerStats)
}

let rivefile: Rive | null = null
let _stateMachiveInputs: StateMachineInput[] = reactive([])
const canvasElement = ref<HTMLCanvasElement | null>(null)

function initTestRive() {
  rivefile = new Rive({
    src: '/rive/pet-abode-baby.riv',
    canvas: canvasElement.value as HTMLCanvasElement,
    autoplay: true,
    stateMachines: 'Baby',
    onLoad: () => {
      if (!rivefile) {
        console.log('issue')
        return
      }
      _stateMachiveInputs = rivefile.stateMachineInputs('Baby')
    },
  })
}

watch(canvasElement, canvasElement => {
  if (!canvasElement) return
  nextTick(() => {
    initTestRive()
  })
})

// Live Ops
const userEvents = ref<LiveOpsUserEvent[]>([])
const eventTemplates = ref<LiveOpsEventTemplate[]>([])

async function fetchLiveOpsEvents() {
  try {
    const response = await api.liveOpsGetUserEvents()
    userEvents.value = response.userEvents
    eventTemplates.value = response.eventTemplates
  } catch (error) {
    console.error('Failed to buy mini game entry:', error)
    webapp.showAlert('Failed to enter the game. ' + error)
  }
}
</script>

<template>
  <div>
    <div>
      <div>Your user id: {{ profile.id }}</div>
      <div>Your jettons</div>
      <div v-if="balances.length === 0">
        You have no jettons
      </div>
    </div>

    <br>
    {{ WebApp.version }}
    {{ webapp.language }}
    <br>
    <br>
    <span class="whitespace-pre-line">
      {{
        `Screen size:
    ${height} h
    ${width} w
    ${webapp.safeAreaInset.top} safe area top
    ${webapp.contentSafeAreaInset.top} content safe area top
    ${webapp.safeAreaInset.bottom} safe area bottom
    ${webapp.contentSafeAreaInset.bottom} content safe area bottom`
      }}
    </span>
    <br>

    <BaseButton @click="getFreeStars">
      Get 500 stars
    </BaseButton>
    <BaseButton @click="fullScreen">
      Go full screen
    </BaseButton>
  </div>
</template>

<style scoped>

</style>
